import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { Box, Drawer, Tabs, TabList, Tab, TabPanel, Grid, Divider, IconButton, Typography, Card } from '@mui/joy';

import CircularProgress from '@mui/joy/CircularProgress';
import { toast } from 'react-toastify';

/* Icons */
import ModeIcon from '@mui/icons-material/Mode';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AnalyticsIcon from '@mui/icons-material/Analytics';

// import mobile, tablet and desktop icons
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo';

import CloseIcon from '@mui/icons-material/Close';
import useUploadFile from '@/utils/useUploadFile';


/* Components */
import EditPost from './EditPost';
import PostStrength from './PostStrength';
import PreviewPost from './Preview';
import PostSaveSchedule from './PostSaveSchedule';
import PostScheduleModal from './PostScheduleModal';
import PostNowModal from './PostNowModal';
import { useSelectedUserAccount } from '@/contexts/SelectedUserAccountContext';
import { sanitizePost } from "@/utils/sanitizePost";

const ManagePostModal = ({ open, toggleDrawer, content, setContent, session, postId, fetchPosts }) => {
  const [deviceView, setDeviceView] = useState('desktop');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isPostNowModalOpen, setIsPostNowModalOpen] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(postId);
  const [postMedia, setPostMedia] = useState({
    photos: [],
    video: '',
    pdf: '',
  });
  const [currentMediaType, setCurrentMediaType] = useState(null);
  const [linkPreview, setLinkPreview] = useState({ title: '', image: '', link_url: '' });
  const [postStrength, setPostStrength] = useState({});
  const [postStrengthLoading, setPostStrengthLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState('post');
  const [showPostStrength, setShowPostStrength] = useState(false);
  const { file, setFile, uploadFile } = useUploadFile();
  const { selectedUser, selectUserAccount } = useSelectedUserAccount();

  const newDraft = () => {
    setCurrentPostId(null);
    setContent('');
    setPostMedia({
      photos: [],
      video: '',
      pdf: '',
    });
  };

  const handleSavePost = async () => {

    if (!content || content.trim().length === 0) {
      toast.error("Post content cannot be empty.");
      return;
    }

    let postId = currentPostId;
    if (!postId) {
      postId = await createNewPost();
      setCurrentPostId(postId);
    }

    await updatePost(postId, sanitizePost(content), fetchPosts);

    if (postMedia.photos.length > 0 || postMedia.video || postMedia.pdf) {
      await updateMedia(postId, postMedia);
    }
  };

  const fetchMedia = async (postId) => {
    try {
      const response = await fetch(`/api/media/post_media?post_id=${postId}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  };

  const fetchPost = async (postId) => {
    try {
      const response = await fetch(`/api/posts/post?postId=${postId}`);

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching post:', error);
    }
  };


  const updateMedia = async (postId, mediaUpload) => {
    try {
      const result = await fetch('/api/posts/update-media', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post_id: postId,
          media: mediaUpload,
        }),
      });

      const data = await result.json();
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  const updatePost = async (postId, content, fetchPosts) => {
    try {
      const result = await fetch('/api/posts/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post_id: postId,
          post_text: content,
          user_account_id: selectedUser?.user_id || session?.user?.id,
        }),
      });

      const data = await result.json().catch(() => ({}));

      if (result.ok) {
        toast.success(data?.message || 'Post saved successfully!');
        if (typeof fetchPosts === 'function') fetchPosts(); // ✅ safe call
        return data;
      } else {
        const errorMessage =
          data?.error ||
          data?.message ||
          `Error ${result.status}: Unable to save post.`;

        console.error('Error saving post:', errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Unexpected error saving post.');
    }
  };


  const handleUploadFileLink = async (targetLink, link) => {
    try {
      const link_source = link || null;
      const result = await uploadFile(postId, null, targetLink, link_source);

      if (result.success) {
        setPostMedia(prevState => ({
          ...prevState,
          photos: [...prevState.photos, result.url]
        }));
      }

    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const extractUrls = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    if (urlRegex.test(text)) {
      const urls = text.match(urlRegex);
      return urls;
    }

    return [];
  };

  const createNewPost = async () => {
    try {
      const result = await fetch('/api/posts/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await result.json();
      return data.post_id;
    } catch (error) {
      console.error(error);
    }
  };

  const handlePostStrength = async () => {
    setPostStrengthLoading(true);
    setShowPostStrength(true);
    const response = await fetch('/api/posts/post_score', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message: content }),
    });

    const data = await response.json();
    const message = data.response;

    setPostStrength(message);
    setCurrentTab('post_strength');
    setPostStrengthLoading(false);
  }

  const ImageLinkDownload = async (content) => {
    try {
      const urls = extractUrls(content);

      if (urls.length > 0) {
        const mainLink = urls[urls.length - 1];
        const fetchMetadata = async () => {
          try {
            const res = await fetch(`/api/fetch-metadata?url=${encodeURIComponent(mainLink)}`);
            const data = await res.json();
            await handleUploadFileLink(data?.image, mainLink);
          } catch (error) {
            console.error('Error fetching metadata:', error);
          }
        };

        await fetchMetadata();
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };


  useEffect(() => {
    if (postId) {
      fetchMedia(postId).then(mediaData => {
        if (mediaData) {
          setPostMedia({
            photos: mediaData.photos || [],
            video: mediaData.video || '',
            pdf: mediaData.pdf || '',
          });
        }
      });

      fetchPost(postId).then(postData => {
        if (postData) {
          if (postData.post_text) {
            setContent(sanitizePost(postData.post_text || ''));

            const userDetails = {
              user_id: postData.user_id,
              name: postData.user_name, // Assuming postData contains a user name
              profile_image: postData.user_image, // Assuming postData contains a user profile image
            };

            selectUserAccount(userDetails);
          }
        }
      });
    }

  }, [postId]);



  useEffect(() => {
    if (postId === null && postMedia.photos.length === 0 && postMedia.video === '' && postMedia.pdf === '') {
      ImageLinkDownload(content);
    }
  }, []);

  return (
    <Drawer size="lg" open={open} onClose={toggleDrawer(false)} anchor="right" hideBackdrop={false} >

      <PostScheduleModal
        setOpenDrawer={open}
        PostId={currentPostId}
        isScheduleModalOpen={isScheduleModalOpen}
        setIsScheduleModalOpen={setIsScheduleModalOpen}
        session={session}
        fetchPosts={fetchPosts}
      />

      <PostNowModal
        setOpenDrawer={open}
        PostId={currentPostId}
        isPostNowModalOpen={isPostNowModalOpen}
        setIsPostNowModalOpen={setIsPostNowModalOpen}
      />

      <Grid container spacing={1} xs={12} sx={{ display: { xs: 'flex', md: 'none' } }}>


        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', mt: '3.5rem' }}>

          <TabList>
            <Tab value="post">Post</Tab>
            <Tab value="preview">Preview</Tab>
          </TabList>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', p: '5px 20px' }}>
            <IconButton onClick={toggleDrawer(false)} sx={{ position: 'absolute', right: 10, top: '0', zIndex: '99' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TabPanel value="post" sx={{ p: 0, m: 0 }}>
            <EditPost
              postId={currentPostId}
              session={session}
              content={content}
              setContent={setContent}
              postMedia={postMedia}
              setPostMedia={setPostMedia}
              setCurrentMediaType={setCurrentMediaType}
              postStrength={postStrength}
              postStrengthLoading={postStrengthLoading}
              handlePostStrength={handlePostStrength}
              showPostStrength={showPostStrength}
              setShowPostStrength={setShowPostStrength}
            />
            <PostSaveSchedule
              handleSavePost={handleSavePost}
              setIsScheduleModalOpen={setIsScheduleModalOpen}
              setIsPostNowModalOpen={setIsPostNowModalOpen}
              postId={currentPostId}
              newDraft={newDraft}
            />
          </TabPanel>

          <TabPanel value="preview">
            <PreviewPost
              session={session}
              content={content}
              postMedia={postMedia}
              linkPreview={linkPreview}
              deviceView={deviceView}
            />
          </TabPanel>
        </Tabs>
      </Grid>


      <Grid container spacing={2} sx={{ p: 0, m: 0, height: '100vh', display: { xs: 'none', md: 'flex' } }}>

        <Grid xs={12} sm={6} md={7} sx={{ p: 0, m: 0, display: 'flex', flexDirection: 'column', borderRight: { xs: 0, md: '1px solid #e0e0e0' } }}>

          <Box sx={{ flexGrow: 1 }}>
            <EditPost
              postId={currentPostId}
              session={session}
              content={content}
              setContent={setContent}
              postMedia={postMedia}
              setPostMedia={setPostMedia}
              setCurrentMediaType={setCurrentMediaType}
              postStrength={postStrength}
              postStrengthLoading={postStrengthLoading}
              handlePostStrength={handlePostStrength}
              showPostStrength={showPostStrength}
              setShowPostStrength={setShowPostStrength}
            />
          </Box>

          <Box sx={{ width: '100%' }}>
            <PostSaveSchedule
              handleSavePost={handleSavePost}
              setIsScheduleModalOpen={setIsScheduleModalOpen}
              setIsPostNowModalOpen={setIsPostNowModalOpen}
              postId={currentPostId}
              newDraft={newDraft}
            />
          </Box>
        </Grid>

        <Grid xs={12} sm={6} md={5} sx={{ p: 0, m: 0, backgroundColor: '#f9f9f9' }}>
          <Box sx={{ background: 'white', p: '6px 20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography level="title-md">
                Post Preview
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>

                <Typography level="body-xs">
                  Devices:
                </Typography>
                <IconButton
                  onClick={() => setDeviceView('mobile')}
                  sx={{ cursor: 'pointer', backgroundColor: deviceView === 'mobile' ? '#eeeaea' : 'white' }}>
                  <PhoneIphoneIcon sx={{ cursor: 'pointer' }} />
                </IconButton>

                <IconButton onClick={() => setDeviceView('tablet')}
                  sx={{ cursor: 'pointer', backgroundColor: deviceView === 'tablet' ? '#eeeaea' : 'white' }}>
                  <TabletMacIcon sx={{ cursor: 'pointer' }} />
                </IconButton>

                <IconButton
                  onClick={() => setDeviceView('desktop')}
                  sx={{ cursor: 'pointer', backgroundColor: deviceView === 'desktop' ? '#eeeaea' : 'white' }}>
                  <PersonalVideoIcon sx={{ cursor: 'pointer' }} />
                </IconButton>
              </Box>

            </Box>
          </Box>

          <Divider />
          <PreviewPost
            session={session}
            content={content}
            postMedia={postMedia}
            linkPreview={linkPreview}
            deviceView={deviceView}
          />
        </Grid>

      </Grid>

    </Drawer>
  );
};

export default ManagePostModal;
